import { useEffect, useState } from "react"
import { getMentorProfileViewsApi, MentorProfileViews } from "../../features/analytics/api/mentorAnalytics.api"
import { ExclamationTriangleIcon, EyeIcon } from "@heroicons/react/16/solid"

const Stat = ({ label, value }: { label: string; value: string | number }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">
      {typeof value === "number" ? value.toLocaleString("ru") : value}
    </p>
  </div>
)

const Avatar = ({ name }: { name: string }) => {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-blue-100 text-blue-700", "bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700"]
  const cl = colors[name.charCodeAt(0) % colors.length]
  return (
    <div className={`w-8 h-8 rounded-full ${cl} flex items-center justify-center text-xs font-semibold flex-shrink-0`}>
      {initials}
    </div>
  )
}

const RoleBadge = ({ role }: { role: string }) => {
  const map: Record<string, string> = {
    user:   "bg-blue-50 text-blue-700",
    mentor: "bg-violet-50 text-violet-700",
    admin:  "bg-red-50 text-red-600",
  }
  const labels: Record<string, string> = { user: "Пользователь", mentor: "Ментор", admin: "Администратор" }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[role] ?? "bg-gray-100 text-gray-600"}`}>
      {labels[role] ?? role}
    </span>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
}

const LIMIT = 20

export const MentorAnalyticsProfileViewsPage = () => {
  const [data, setData] = useState<MentorProfileViews | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getMentorProfileViewsApi({ limit: LIMIT, offset: 0 })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить просмотры профиля</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !data) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Всего просмотров"      value={data.total_views} />
        <Stat label="Уникальных посетителей" value={data.unique_visitors} />
        <Stat label="За последние 7 дней"   value={data.views_last_7_days} />
      </div>

      {/* Viewers table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Кто просматривал мой профиль</h3>
          <span className="text-xs text-gray-400">{data.viewers.length} чел.</span>
        </div>

        {data.viewers.length === 0 ? (
          <div className="p-10 text-center">
            <EyeIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-300">Просмотров пока нет</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  {["Пользователь", "Роль", "Дата просмотра"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.viewers.map((v) => (
                  <tr key={v.user_id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={v.full_name} />
                        <span className="font-medium text-gray-800">{v.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <RoleBadge role={v.role} />
                    </td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs">{formatDate(v.viewed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
